import { DarkChapter } from "@/components/layout/DarkChapter";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";

export default function PrivacyPolicy() {
  return (
    <DarkChapter>
      <Navigation />
      <main className="relative z-10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-20 lg:py-32">
          {/* Overline Badge */}
          <div className="inline-block px-3 py-1 mb-6 text-[10px] font-mono uppercase tracking-[0.2em] text-white/60 border border-white/20 rounded-full">
            Legal
          </div>

          {/* Title */}
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-2">
            Privacy Policy
          </h1>
          <p className="text-lg text-white/70 mb-1">DK Music Business Academy Ltd</p>
          <p className="text-sm text-white/50 mb-12">Last updated: 21 November 2025</p>

          {/* Introduction */}
          <div className="space-y-4 mb-12">
            <p className="text-base text-white/80 leading-relaxed">
              DK Music Business Academy Ltd respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, and protect your data when you use www.dk-artists.com or engage our services.
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-10">
            {/* Section 1 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                1. Who We Are
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  DK Music Business Academy Ltd
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  Registered office: Palmeira Avenue Mansions, 19 Church Road, Hove, East Sussex, BN3 2FA
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  Email: <a href="mailto:hello@dk-mba.com" className="text-[#0071e3] hover:underline">hello@dk-mba.com</a>
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  Website: www.dk-artists.com
                </p>
                <p className="text-base text-white/80 leading-relaxed mt-4">
                  We are the data controller responsible for your personal data under UK GDPR.
                </p>
              </div>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                2. Information We Collect
              </h2>
              <div className="space-y-4">
                <p className="text-base text-white/80 leading-relaxed">
                  We may collect the following categories of information:
                </p>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Contact information</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Name, email address, phone number, social handles, or any details submitted through forms.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Client project information</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Links, assets, files, brand materials, and information required to complete your project.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Account access information</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Permissions or logins you provide for Meta, TikTok, Google, Spotify, YouTube, websites, and advertising platforms.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Payment information</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Payments are processed securely through Stripe. We do not store full payment card details.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Usage data</h3>
                  <ul className="space-y-2 ml-6">
                    <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                      IP address
                    </li>
                    <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                      Browser type
                    </li>
                    <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                      Device information
                    </li>
                    <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                      Pages viewed
                    </li>
                    <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                      Time spent on the site
                    </li>
                    <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                      Clicks and user interactions
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Marketing and communication preferences</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Your choice to receive emails, updates, or offers.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Cookies and tracking data</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Used for analytics and retargeting. See Section 11.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                3. How We Collect Your Data
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">We collect data when:</p>
                <ul className="space-y-2 ml-6">
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    You fill in forms on our website
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    You purchase services
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    You contact us directly
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    You upload or send project materials
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Automatically through cookies and analytics tools
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Through advertising pixels like Meta Pixel or Google Analytics
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                4. How We Use Your Data
              </h2>
              <div className="space-y-4">
                <p className="text-base text-white/80 leading-relaxed">
                  We use your data for the following purposes:
                </p>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">To deliver our services</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Running ads, managing accounts, creating content, building campaigns, and all project-related tasks.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">To communicate with you</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Onboarding, project updates, support messages, and service notifications.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">To process payments and invoices</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Through Stripe's secure payment system.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">To send marketing communication</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Only if you have given consent. You can unsubscribe at any time.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">To improve our services and website</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Analytics, optimisation, testing, and user experience improvements.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">To meet legal obligations</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Such as tax records and compliance requirements.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                5. Legal Basis for Processing
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  Under UK GDPR we rely on the following legal bases:
                </p>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Performance of a contract</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    To deliver the services you purchase.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Consent</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    For marketing and some tracking tools.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Legitimate interest</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    For analytics, communication, fraud prevention, and improving services.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Legal obligation</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    For tax, accounting, and regulatory compliance.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                6. How We Share Your Data
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed font-semibold">
                  We do not sell your personal data.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  Data may be shared with:
                </p>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Trusted service providers</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Including Stripe, ActiveCampaign, Meta, Google, YouTube, TikTok, Wix, Supabase, and any required tools used to deliver your services.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Approved contractors or team members</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Only when required for your project.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Regulators or authorities</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Only if legally required.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                7. Processing Client Data for Services
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  If you give us fan data, customer lists, email subscribers, or platform access:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    You confirm that you have the legal right to share this data.
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    We act only as a data processor.
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    We will never use your data for any purpose outside your project.
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    We will not export, sell, or share your data except where necessary to complete your agreed services.
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                8. International Data Transfers
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  Some service providers may store data outside the UK or EEA.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  We ensure lawful safeguards such as:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Standard Contractual Clauses
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Encryption
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Approved transfer frameworks
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                9. Data Retention
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  We retain your data only for as long as necessary:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    To deliver your services
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    To meet legal, tax, and accounting requirements
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    To resolve any disputes
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    To maintain business records
                  </li>
                </ul>
                <p className="text-base text-white/80 leading-relaxed mt-4">
                  Marketing data is retained until you unsubscribe or request deletion.
                </p>
              </div>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                10. Your Rights Under UK GDPR
              </h2>
              <div className="space-y-4">
                <p className="text-base text-white/80 leading-relaxed">You have the right to:</p>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Access your data</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Request a copy of the information we hold.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Correct inaccurate information</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Request updates or corrections.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Request deletion</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Ask for your data to be removed where legally appropriate.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Restrict processing</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Limit how your data is used.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Object to processing</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    For example, you can opt out of marketing at any time.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Data portability</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Receive your data in a transferable format.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white/90 mb-2">Withdraw consent</h3>
                  <p className="text-base text-white/75 leading-relaxed">
                    Applies to marketing communication and optional tracking.
                  </p>
                </div>

                <p className="text-base text-white/80 leading-relaxed mt-4">
                  To exercise your rights, email: <a href="mailto:hello@dk-mba.com" className="text-[#0071e3] hover:underline">hello@dk-mba.com</a>
                </p>
              </div>
            </section>

            {/* Section 11 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                11. Cookies and Tracking Technologies
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  We use cookies and tracking tools for:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Website functionality
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Performance analytics
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Retargeting
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Advertising optimisation
                  </li>
                </ul>
                <p className="text-base text-white/80 leading-relaxed mt-4">
                  These may include:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Meta Pixel
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Google Analytics
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    YouTube tracking
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    TikTok tracking
                  </li>
                </ul>
                <p className="text-base text-white/80 leading-relaxed mt-4">
                  You can manage or disable cookies through your browser settings.
                </p>
              </div>
            </section>

            {/* Section 12 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                12. Security Measures
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  We take appropriate steps to protect your data, including:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Secure servers
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Encrypted data storage
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Password protection
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Two factor authentication
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Restricted access to sensitive systems
                  </li>
                </ul>
                <p className="text-base text-white/80 leading-relaxed mt-4">
                  While no online system is completely secure, we take all reasonable measures to safeguard your information.
                </p>
              </div>
            </section>

            {/* Section 13 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                13. Third-Party Links
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  Our website may link to external sites.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  We are not responsible for the privacy practices of external websites.
                </p>
              </div>
            </section>

            {/* Section 14 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                14. Children's Information
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  Our services are not intended for individuals under 18.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  We do not knowingly collect data from minors.
                </p>
              </div>
            </section>

            {/* Section 15 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                15. Updates to This Policy
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  We may update this Privacy Policy when required.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  The latest version will always be posted on this page.
                </p>
              </div>
            </section>

            {/* Section 16 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                16. Contact Us
              </h2>
              <div className="bg-white/5 border border-white/10 rounded-lg p-6 space-y-2">
                <p className="text-base text-white/90 leading-relaxed">
                  For any privacy questions or data requests, contact:
                </p>
                <p className="text-base text-white/90 leading-relaxed">
                  DK Music Business Academy Ltd
                </p>
                <p className="text-base text-white/90 leading-relaxed">
                  Email: <a href="mailto:hello@dk-mba.com" className="text-[#0071e3] hover:underline">hello@dk-mba.com</a>
                </p>
                <p className="text-base text-white/90 leading-relaxed">
                  Registered office: Palmeira Avenue Mansions, 19 Church Road, Hove, East Sussex, BN3 2FA
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </DarkChapter>
  );
}
